import { types as api } from '@mesa-labs/mesa-api';
import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Card,
  CardContainer, CardTitle,
  DownloadButton,
  MesaColumn,
  MesaTable,
  ResourceCard,
  ResourceSection,
  useMesaTableServerState,
} from '@mesa-labs/mesa-ui';
import { ResourceItemProp } from '@mesa-labs/mesa-ui/dist/components/ResourceCard';

import {
  useGetExternalVendorQuery, useGetExternalVendorClientsQuery,
  useGetExternalVendorInvoicesQuery,
  exportExternalVendorInvoices,
} from '../../redux/api/externalVendors';
import { useDispatch, useSelector } from '../../redux/hooks';
import {
  updateCurrentExternalVendorId,
  updateExternalInvoicesServicedByMesa,
} from '../../redux/slices/externalVendors';
import { DefaultPaginationLimits } from './PaginationLimits';
import { FilterCell, FilterControls, FilterControlsButtonContainer, FilterRow, FilterSection } from './Filters';
import OptionalBooleanDropdown from './OptionalBooleanDropdown';
import { asExportFilter, stackedHorizontalBarChartOptions } from '../../utils';
import ReactApexChart from 'react-apexcharts';
import MetricStatTable, { MetricGroup } from './MetricStatTable';

const ClientPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ExternalVendorClientMesaColumns: MesaColumn<api.ExternalVendorClientDataResponse>[] = [
  {
    id: 'partnerName',
    label: 'Partner',
    sortField: 'partnerId',
    accessorFn: (row) => api.getPartnerName(row.partnerId),
  },
  {
    key: 'externalClientId', label: 'External Client Id', sortField: 'externalClientId',
  },
  {
    key: 'externalClientName', label: 'External Client Name', sortField: 'externalClientName', flex: true,
  },
  {
    key: 'segment', label: 'Client Segment', sortField: 'segment',
  },
  {
    key: 'defaultPaymentTermsDays', label: 'Default Payment Terms', sortField: 'defaultPaymentTermsDays',
  },
  {
    key: 'totalInvoiceSpend', label: 'Vendor Invoice Spend (T12M)', type: 'currency', sortField: 'totalInvoiceSpend', currencyCode: 'USD',
  },
  {
    key: 'totalInvoiceSpendT6m', label: 'Vendor Invoice Spend (T6M)', type: 'currency', sortField: 'totalInvoiceSpendT6m', currencyCode: 'USD',
  },
];

const ExternalVendorInvoiceMesaColumns: MesaColumn<api.ExternalInvoiceDataResponse>[] = [
  {
    key: 'invoiceNumber', label: 'Invoice Number', sortField: 'invoiceNumber', flex: true,
  },
  {
    key: 'invoiceDate', label: 'Invoice Date', type: 'date', sortField: 'invoiceDate',
  },
  {
    key: 'generalLedgerDate', label: 'General Ledger Date', type: 'date', sortField: 'generalLedgerDate',
  },
  {
    key: 'dueDate', label: 'Due Date', type: 'date', sortField: 'dueDate',
  },
  {
    key: 'paymentDate', label: 'Payent Date', type: 'date', sortField: 'paymentDate',
  },
  {
    key: 'grossAmount', label: 'Amount', type: 'currency', sortField: 'grossAmount', currencyCode: 'USD',
  },
  {
    key: 'servicedByMesa', label: 'Service By Mesa', sortField: 'servicedByMesa',
  },
];

type ExternalVendorPageTemplateProps = {
  businessEntityScope: api.BusinessEntityScope;
};

function ExternalVendorPageTemplate(props: ExternalVendorPageTemplateProps): React.ReactElement | null {
  const { businessEntityScope } = props;
  const { id: externalVendorId, partnerId } = useParams();
  const dispatch = useDispatch();
  const externalInvoicesServicedByMesa = useSelector((state) => state.externalVendors.externalInvoicesServicedByMesa);
  const partnerIdParam = parseInt(partnerId || '', 10);

  const externalClientsServer = useMesaTableServerState({
    initialPageSize: 10,
    initialSorting: [{ id: 'totalInvoiceSpendT6m', desc: true }],
    columns: ExternalVendorClientMesaColumns,
    persistKey: 'externalVendorPageTemplate-clients',
  });

  const externalInvoicesServer = useMesaTableServerState({
    initialPageSize: 10,
    initialSorting: [{ id: 'paymentDate', desc: true }],
    columns: ExternalVendorInvoiceMesaColumns,
    persistKey: 'externalVendorPageTemplate-invoices',
  });

  useEffect(() => {
    if (externalVendorId) {
      dispatch(updateCurrentExternalVendorId(externalVendorId));
    }
  }, [externalVendorId]);

  const {
    data: externalVendor,
  } = useGetExternalVendorQuery({
    partnerId: partnerIdParam,
    externalVendorId: externalVendorId!,
    businessEntityScope
  }, { skip: !externalVendorId });

  const {
    data: {
      data: externalClients,
      total: totalExternalClients,
    } = {},
    isLoading: isGetExternalVendorClientsLoading,
    isFetching: isGetExternalVendorClientsFetching,
  } = useGetExternalVendorClientsQuery({
    partnerId: partnerIdParam,
    externalVendorId: externalVendorId!,
    excluded: false,
    ...externalClientsServer.queryArgs,
    sortDirection: externalClientsServer.queryArgs.sortDirection as api.SortDirection | undefined,
    businessEntityScope
  }, { skip: !externalVendorId });

  const externalInvoicesFilter = {
    ...externalInvoicesServer.queryArgs,
    sortDirection: externalInvoicesServer.queryArgs.sortDirection as api.SortDirection | undefined,
    partnerId: partnerIdParam,
    externalVendorId: externalVendorId!,
    businessEntityScope,
    servicedByMesa: externalInvoicesServicedByMesa,
  };
  const {
    data: {
      data: externalInvoices,
      total: totalExternalInvoices,
    } = {},
    isLoading: isGetExternalVendorInvoicesLoading,
    isFetching: isGetExternalVendorInvoicesFetching,
  } = useGetExternalVendorInvoicesQuery(externalInvoicesFilter, { skip: !externalVendorId });

  const timelineSeries: ApexAxisChartSeries = useMemo(() => {
    if (!externalVendor) {
      return [];
    }

    return [
      {
        name: 'Weighted Average Approval Time',
        data: [{ x: 'Time (Days)', y: externalVendor.weightedAverageApprovalTime }]
      },
      {
        name: 'Weighted Average Import Lateness',
        data: [{ x: 'Time (Days)', y: externalVendor.weightedAverageImportLateness }]
      },
      {
        name: 'Weighted Average Pre-Float Period',
        data: [{ x: 'Time (Days)', y: externalVendor.weightedAveragePreFloatPeriod }]
      },
      {
        name: 'Weighted Average Float Period',
        data: [{ x: 'Time (Days)', y: externalVendor.weightedAverageFloatPeriod }]
      },
    ];
  }, [externalVendor]);

  if (!externalVendor) {
    return null;
  }

  const externalVendorItems: Array<[string, Array<string | ResourceItemProp>]> =
    [
      ['Basic Info',
        [
          { key: 'parterName', label: 'Partner' },
          { key: 'externalVendorId' },
          { key: 'internalVendorState', label: 'Internal Status', type: 'status' },
          { key: 'internalVendorId', label: 'Internal Vendor Link', type: 'link' },
          'email',
          'phoneNumber',
          { key: ['address', 'addressAdditional', 'city', 'state', 'zip', 'countryCode'], width: '50%' },
          { key: 'paymentMethod', type: 'status' },
          'taxId',
          'locale',
          'withholdingPercent',
          'classification',
          'serviceType',
          { key: 'diverse', label: 'Diverse?' },
          { key: 'hubSpotRecordId', type: 'link', width: '50%' },
        ]
      ],
    ];

  const invoiceVolumeGroups: MetricGroup[] = [
    {
      label: 'Counts',
      rows: [
        { label: 'Eligible Invoice Count', t6m: externalVendor.eligibleInvoiceCountT6m, t12m: externalVendor.eligibleInvoiceCountT12m, format: 'count' },
        { label: 'Invoices Accelerated', allTime: externalVendor.invoicesAccelerated, format: 'count' },
      ],
    },
    {
      label: 'Spend',
      rows: [
        { label: 'Eligible Spend', t6m: externalVendor.eligibleInvoiceSpendT6m, t12m: externalVendor.eligibleInvoiceSpend, format: 'currency' },
        { label: 'Monthly Avg Spend', t6m: externalVendor.monthlyInvoiceSpendT6m, t12m: externalVendor.monthlyInvoiceSpendT12m, format: 'currency' },
        { label: 'Monthly Spend Std Dev', t6m: externalVendor.monthlyInvoiceSpendStdDevT6m, t12m: externalVendor.monthlyInvoiceSpendStdDevT12m, format: 'currency' },
        { label: 'Total Invoice Amount (Prior Year)', t12m: externalVendor.totalInvoiceAmountPriorYear, format: 'currency' },
        { label: 'Total Invoice Amount (Current Year)', t12m: externalVendor.totalInvoiceAmountCurrentYear, format: 'currency' },
      ],
    },
    {
      label: 'Revenue',
      rows: [
        { label: 'Amount Accelerated', allTime: externalVendor.invoiceAmountAccelerated, format: 'currency' },
        { label: 'Revenue Generated', allTime: externalVendor.revenueGenerated, format: 'currency' },
      ],
    },
  ];

  const timelineGroups: MetricGroup[] = [
    {
      label: 'Eligible Weighted Averages',
      rows: [
        { label: 'Payment Tenor (WAPT)', t6m: externalVendor.weightedEligibleAveragePaymentTenorT6m, t12m: externalVendor.weightedEligibleAveragePaymentTenor, format: 'days' },
        { label: 'Invoice Tenor (WAIT)', t6m: externalVendor.weightedEligibleAverageInvoiceTenorT6m, t12m: externalVendor.weightedEligibleAverageInvoiceTenor, format: 'days' },
        { label: 'Ledger Lateness (WALL)', t6m: externalVendor.weightedEligibleAverageLedgerLatenessT6m, t12m: externalVendor.weightedEligibleAverageLedgerLatenessT12m, format: 'days' },
      ],
    },
    {
      label: 'Internal Pipeline',
      rows: [
        { label: 'Approval Time', allTime: externalVendor.weightedAverageApprovalTime, format: 'days' },
        { label: 'Import Lateness', allTime: externalVendor.weightedAverageImportLateness, format: 'days' },
        { label: 'Pre-Float Period', allTime: externalVendor.weightedAveragePreFloatPeriod, format: 'days' },
        { label: 'Float Period', allTime: externalVendor.weightedAverageFloatPeriod, format: 'days' },
      ],
    },
  ];

  const yieldRiskGroups: MetricGroup[] = [
    {
      label: 'Pricing',
      rows: [
        { label: 'Weighted Discount Rate', allTime: externalVendor.weightedAverageDiscountRate, format: 'percentage' },
        { label: 'Weighted Average APR', allTime: externalVendor.weightedAverageApr, format: 'percentage' },
        { label: 'Net 30 APR', t6m: externalVendor.weightedEligibleAverageNet30AprT6m, t12m: externalVendor.weightedEligibleAverageNet30AprT12m, format: 'percentage' },
        { label: 'Net 30 Yield Pricing', t6m: externalVendor.weightedEligibleAverageNet30YieldPricingT6m, t12m: externalVendor.weightedEligibleAverageNet30YieldPricingT12m, format: 'percentage' },
        { label: '% Projected Eligible Spend', t6m: externalVendor.percentageProjectedEligibleInvoiceSpendT6m, format: 'percentage' },
      ],
    },
    {
      label: 'Invoice Failure Rates',
      rows: [
        { label: 'Internal Invoices Failing Net-30', allTime: externalVendor.internalPercentageFailingNet30, format: 'percentage' },
        { label: 'Internal Invoices Failing Net-60', allTime: externalVendor.internalPercentageFailingNet60, format: 'percentage' },
        { label: 'Internal Invoices Settled In 3', allTime: externalVendor.internalPercentageSettledWithin3, format: 'percentage' },
        { label: 'External Invoices Failing Net-30', allTime: externalVendor.externalPercentageFailingNet30, format: 'percentage' },
        { label: 'External Invoices Failing Net-60', allTime: externalVendor.externalPercentageFailingNet60, format: 'percentage' },
        { label: 'External Invoices Settled In 3', allTime: externalVendor.externalPercentageSettledWithin3, format: 'percentage' },
      ],
    },
  ];

  const externalVendorAuditItems: (string | ResourceItemProp)[] = [
    { key: 'signedUpAt', type: 'datetime' },
    { key: 'activatedAt', type: 'datetime' },
    { key: 'deactivatedAt', type: 'datetime', width: '50%' },
  ];

  return (
    <ClientPageContainer>
      <CardContainer>
        <ResourceCard
          resource={{
            ...externalVendor,
            internalVendorIdLocation: externalVendor.internalVendorId ? `vendors/${externalVendor.internalVendorId}` : undefined,
            hubSpotRecordIdLocation: externalVendor.hubSpotRecordId ? `https://app.hubspot.com/contacts/21815702/record/0-2/${externalVendor.hubSpotRecordId}` : undefined,
            parterName: api.getPartnerName(externalVendor.partnerId),
            isInternalVendor: !!externalVendor.internalVendorId && !externalVendor.deactivatedAt,
            currency: 'USD', // TODO: MESA-1457: Multi-currency support
          }}
          title={externalVendor.externalVendorName || externalVendor.externalVendorId}
          items={[]}
          sectionedItems={externalVendorItems}
          auditItems={externalVendorAuditItems}
          columns={4}
        />
      </CardContainer>
      <MetricStatTable persistKey="externalVendorInvoiceVolumeSpend" title="Invoice Volume & Spend" groups={invoiceVolumeGroups} />
      <MetricStatTable persistKey="externalVendorPaymentTimeline" title="Payment Timeline" subtitle="Weighted averages · days" groups={timelineGroups} />
      <MetricStatTable persistKey="externalVendorYieldRisk" title="Yield & Risk" groups={yieldRiskGroups} />
      <CardContainer>
        <Card minHeight="400px">
          <CardTitle>Weighted Average Invoice Timeline (Internal)</CardTitle>
          <ReactApexChart options={stackedHorizontalBarChartOptions()} series={timelineSeries} type="bar"
            height={350} />
        </Card>
      </CardContainer>
      <ResourceSection
        title="Clients (T12M - Included Only)"
      >
        <MesaTable
          columns={ExternalVendorClientMesaColumns}
          data={externalClients || []}
          dynamicColumnMode
          getRowId={(row) => `${row.partnerId}:${row.externalClientId}`}
          isLoading={isGetExternalVendorClientsLoading || isGetExternalVendorClientsFetching}
          {...externalClientsServer.bind}
          pagination={{
            ...externalClientsServer.bind.pagination,
            total: totalExternalClients,
            pageSizeOptions: DefaultPaginationLimits.map((l) => l.value),
          }}
          columnLayout={{ enableResizing: true, enableVisibilityToggle: true, enablePinning: true }}
          minColumnWidth={90}
        />
      </ResourceSection>

      <ResourceSection
        title="Invoices"
      >
        <FilterSection
          filterControl={(
            <FilterControls>
              <FilterControlsButtonContainer>
                <DownloadButton
                  text="Download CSV"
                  data={() => exportExternalVendorInvoices(partnerIdParam, externalVendorId!, {
                    ...asExportFilter(externalInvoicesFilter),
                  })}
                  fileName={`external-vendor-${externalVendorId}-invoicess.csv`}
                  display="purple"
                />
              </FilterControlsButtonContainer>
            </FilterControls>
          )}
        >
          {businessEntityScope !== api.BusinessEntityScope.Internal ? (
            <FilterRow columns={4}>
              <FilterCell>
                <OptionalBooleanDropdown
                  label="Serviced By Mesa"
                  selectedOption={externalInvoicesServicedByMesa}
                  onChange={(value) => dispatch(updateExternalInvoicesServicedByMesa(value))}
                />
              </FilterCell>
            </FilterRow>
          ) : (<></>)}
        </FilterSection>

        <MesaTable
          columns={ExternalVendorInvoiceMesaColumns}
          data={externalInvoices || []}
          dynamicColumnMode
          getRowId={(row) => `${row.partnerId}:${row.externalClientId}:${row.externalDocumentId}:${row.documentType}`}
          isLoading={isGetExternalVendorInvoicesLoading || isGetExternalVendorInvoicesFetching}
          {...externalInvoicesServer.bind}
          pagination={{
            ...externalInvoicesServer.bind.pagination,
            total: totalExternalInvoices,
            pageSizeOptions: DefaultPaginationLimits.map((l) => l.value),
          }}
          columnLayout={{ enableResizing: true, enableVisibilityToggle: true, enablePinning: true }}
          minColumnWidth={90}
        />
      </ResourceSection>
    </ClientPageContainer >
  );
}

export default ExternalVendorPageTemplate;
